import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaExclamationTriangle, FaRedo } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import './Failedcertificates.css';

// ══════════════════════════════════════
//  CONSTANTS
// ══════════════════════════════════════
const API = 'https://digitalapi.definiteassurance.com/api';
const COMPANY = 'Definite';
const ROWS_PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 400;
const CERT_WEBHOOK = 'https://workflows.uuidesign.xyz/webhook/c924c7c4-d115-4724-a78c-27048b99b6f0';

// ══════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════
const formatISODate = (input) => {
  try {
    if (!input) return '--';
    if (typeof input === 'string') return input.split('T')[0];
    if (input instanceof Date) return input.toISOString().split('T')[0];
    return '--';
  } catch { return '--'; }
};

const nowTimestamp = () => {
  const d = new Date();
  const pad = (n, l = 2) => String(n).padStart(l, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
};

const buildPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  const s = Math.max(2, current - 1);
  const e = Math.min(total - 1, current + 1);
  if (s > 2) pages.push('...');
  for (let i = s; i <= e; i++) pages.push(i);
  if (e < total - 1) pages.push('...');
  pages.push(total);
  return pages;
};

// ══════════════════════════════════════
//  COMPONENT
// ══════════════════════════════════════
export default function Failedcertificates() {
  const navigate = useNavigate();

  // ── Auth & agent ──
  const [credentials, setCredentials] = useState(null);
  const [isAuth, setIsauth] = useState(false);
  const [agent, setAgent] = useState('');
  const [agentCode, setAgentcode] = useState('');
  const [agentName, setAgentname] = useState('');

  // ── Data ──
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  // ── Search & pagination ──
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // ── Retry modal ──
  const [genModal, setGenModal] = useState(false);
  const [genStatus, setGenStatus] = useState('idle'); // idle | pending | success | error
  const [certURL, setCertURL] = useState('');
  const [certError, setCertError] = useState('');
  const [retryingOID, setRetryingOID] = useState(null);

  // ── Edit modal ──
  const [editModal, setEditModal] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [proposal, setProposal] = useState('');
  const [insuredItem, setInsuredItem] = useState('');
  const [pin, setPin] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [client, setClient] = useState('');
  const [certNo, setCertNo] = useState('');
  const [periodOfCover, setPeriodOfCover] = useState('');
  const [commencementDate, setCommencementDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [note, setNote] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Debounce ref
  const debounceRef = useRef(null);

  // ══════════════════════════════════════
  //  AUTH HEADERS
  // ══════════════════════════════════════
  const authHeaders = useCallback(() => {
    const base = { 'Content-Type': 'application/json' };
    return credentials ? { ...base, Authorization: `Basic ${credentials}` } : base;
  }, [credentials]);

  // ══════════════════════════════════════
  //  CREDENTIALS
  // ══════════════════════════════════════
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/getCredentials?username=${COMPANY}`);
        const data = await res.json();
        setCredentials(btoa(`${data.Username}:${data.Password}`));
        setIsauth(true);
      } catch {
        toast.error('Failed to initialise authentication.');
      }
    })();
  }, []);

  // ══════════════════════════════════════
  //  AGENT FROM LOCAL STORAGE
  // ══════════════════════════════════════
  useEffect(() => {
    const user = localStorage.getItem('definiteagent');
    if (user) {
      setAgent(user);
    } else {
      navigate('/login');
    }
  }, [navigate]);

  // ══════════════════════════════════════
  //  AGENT CODE
  // ══════════════════════════════════════
  useEffect(() => {
    if (!agent || !isAuth) return;
    (async () => {
      try {
        const res = await fetch(`${API}/getDealerdetails?email=${agent}`, { headers: authHeaders() });
        const data = await res.json();
        setAgentcode(data.OID);
        setAgentname(data.Dealer_name || '');
      } catch {
        toast.error('Failed to load agent details.');
      }
    })();
  }, [agent, isAuth, authHeaders]);

  // ══════════════════════════════════════
  //  FETCH FAILED CERTIFICATES
  // ══════════════════════════════════════
  const fetchCertificates = useCallback(async () => {
    if (!agentCode || !isAuth) return;
    setLoading(true);
    setFetchError('');
    try {
      const res = await fetch(`${API}/getFailedcertificates?code=${agentCode}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Server error');
      const data = await res.json();
      setCertificates(Array.isArray(data) ? data : []);
    } catch {
      setFetchError('Failed to load certificates. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [agentCode, isAuth, authHeaders]);

  useEffect(() => { fetchCertificates(); }, [fetchCertificates]);

  // ══════════════════════════════════════
  //  SEARCH (debounced)
  // ══════════════════════════════════════
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    setCurrentPage(1);
    clearTimeout(debounceRef.current);
    if (!val.trim()) {
      fetchCertificates();
      return;
    }
    debounceRef.current = setTimeout(async () => {
      if (!isAuth) return;
      setLoading(true);
      setFetchError('');
      try {
        const res = await fetch(`${API}/getSearchcertificates?code=${agentCode}&search=${val}`, { headers: authHeaders() });
        const data = await res.json();
        setCertificates(Array.isArray(data) ? data : []);
      } catch {
        setFetchError('Search failed. Please try again.');
      } finally {
        setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
  };

  // ══════════════════════════════════════
  //  PAGINATION (client-side on displayed data)
  // ══════════════════════════════════════
  const totalPages = Math.max(1, Math.ceil(certificates.length / ROWS_PER_PAGE));
  const indexOfFirst = (currentPage - 1) * ROWS_PER_PAGE;
  const currentRows = useMemo(
    () => certificates.slice(indexOfFirst, indexOfFirst + ROWS_PER_PAGE),
    [certificates, indexOfFirst]
  );
  const pageNumbers = useMemo(() => buildPageNumbers(currentPage, totalPages), [currentPage, totalPages]);

  // ══════════════════════════════════════
  //  LOGS
  // ══════════════════════════════════════
  const insertLog = useCallback(async (agentOID, activity) => {
    try {
      await fetch(`${API}/insertLogs?activity=${encodeURIComponent(activity)}&agent=${agentOID}`, {
        headers: authHeaders(),
      });
    } catch { /* non-critical */ }
  }, [authHeaders]);

  // ══════════════════════════════════════
  //  INSERT CERTIFICATE RECORD
  // ══════════════════════════════════════
  const insertCertificate = useCallback(async (certLink, certNoVal, proposalOID, validFrom, expiry, insuredOID) => {
    try {
      const today = nowTimestamp();
      await fetch(`${API}/insertCertificate`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ proposalOID, insuredOID, expiryUnix: expiry, commencementUnix: validFrom, certNo: certNoVal, certLink, today }),
      });
    } catch { /* non-critical */ }
  }, [authHeaders]);

  // ══════════════════════════════════════
  //  RETRY GENERATION
  // ══════════════════════════════════════
  const handleRetry = useCallback(async (insuredOID) => {
    setRetryingOID(insuredOID);
    setGenStatus('pending');
    setCertURL('');
    setCertError('');
    setGenModal(true);
    try {
      const res = await fetch(`${API}/getCertdetails?insured=${insuredOID}`, { headers: authHeaders() });
      const data = await res.json();
      const { Proposal, ValidFrom, ExpiryDate, Capacity, Tonnage, SumInsured } = data;

      await insertLog(agentCode, `Starting to generate Certificate for ${Proposal} by retry`);

      const res3 = await axios.post(CERT_WEBHOOK, {
        IOID: insuredOID,
        commencementUnix: ValidFrom,
        expiryUnix: ExpiryDate,
        sumInsured: SumInsured,
        capacity: Capacity,
        tonnage: Tonnage,
      });

      if (res3.data.success) {
        await insertCertificate(res3.data.certURL, res3.data.certNo, Proposal, ValidFrom, ExpiryDate, insuredOID);
        await insertLog(agentCode, `Certificate for ${Proposal} generated by retry`);
        setCertURL(res3.data.certURL);
        setTimeout(() => setGenStatus('success'), 1500);
      } else {
        setGenStatus('error');
        setCertError(res3.data.errorMessage || 'Unknown error');
        await insertLog(agentCode, `Certificate retry error - ${res3.data.errorMessage}`);
      }
    } catch (err) {
      setGenStatus('error');
      setCertError('An unexpected error occurred. Please try again.');
    }
  }, [agentCode, authHeaders, insertLog, insertCertificate]);

  const closeGenModal = () => {
    setGenModal(false);
    setGenStatus('idle');
    setCertURL('');
    setCertError('');
    setRetryingOID(null);
    if (genStatus === 'success') fetchCertificates();
  };

  // ══════════════════════════════════════
  //  EDIT DETAILS
  // ══════════════════════════════════════
  const handleEdit = useCallback(async (proposalOID) => {
    setProposal(proposalOID);
    setShowDatePicker(false);
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API}/getCertificatedetails?proposal=${proposalOID}`, { headers: authHeaders() }),
        fetch(`${API}/getFailedcertnote?proposal=${proposalOID}`, { headers: authHeaders() }),
      ]);
      const d1 = await r1.json();
      const d2 = await r2.json();
      const { InsuredItem, DealerPin, Email, PeriodOfCover, Client, CertNo } = d1;
      const { CommencementDate, ExpiryDate, Note } = d2;
      setInsuredItem(InsuredItem);
      setPin(DealerPin);
      setClientEmail(Email);
      setPeriodOfCover(PeriodOfCover);
      setClient(Client);
      setCertNo(CertNo);
      setCommencementDate(CommencementDate);
      setExpiryDate(ExpiryDate);
      setNote(Note);
      setEditModal(true);
    } catch {
      toast.error('Failed to load certificate details.');
    }
  }, [authHeaders]);

  const handleCommencementChange = (e) => {
    const val = e.target.value;
    const dateObj = new Date(val);
    const iso = dateObj.toISOString();
    setCommencementDate(iso);
    if (periodOfCover) {
      const exp = new Date(dateObj);
      exp.setDate(exp.getDate() + parseInt(periodOfCover, 10));
      setExpiryDate(exp.toISOString());
    }
  };

  const saveChanges = async () => {
    setEditSaving(true);
    try {
      const res = await fetch(`${API}/updateFailedCert`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ commencementDate, expiryDate, proposal, clientEmail, pin, note, client, certNo }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Details updated successfully!');
        setEditModal(false);
        fetchCertificates();
      } else {
        toast.error('Failed to update details. Please try again.');
      }
    } catch {
      toast.error('An error occurred. Please try again.');
    } finally {
      setEditSaving(false);
    }
  };

  // ══════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════
  return (
    <div className="failed-page">
      <ToastContainer position="top-right" autoClose={4000} />
      <Header userName={agentName || agent} />
      <Sidebar />

      <main className="failed-main">
        {/* Breadcrumb */}
        <div className="failed-top-row">
          <span className="failed-company">Definite Assurance</span>
          <span className="failed-breadcrumb">Dashboard &rsaquo; Failed Certificates</span>
        </div>

        <div className="failed-card">
          <h3><FaExclamationTriangle /> Failed Certificates</h3>

          {/* Toolbar */}
          <div className="failed-toolbar">
            <div className="failed-search-wrap">
              <FaSearch className="failed-search-icon" />
              <input
                type="text"
                placeholder="Search by registration…"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
            <span className="failed-stat">
              Total: <strong>{certificates.length}</strong>
            </span>
          </div>

          {/* Table */}
          {loading ? (
            <div className="failed-loading">
              <div className="failed-spinner" />
              <p>Loading failed certificates…</p>
            </div>
          ) : fetchError ? (
            <div className="failed-error-state">
              <p>{fetchError}</p>
              <button className="failed-btn-retry" onClick={fetchCertificates}>Retry</button>
            </div>
          ) : certificates.length === 0 ? (
            <div className="failed-empty">
              {searchTerm ? `No results for "${searchTerm}".` : 'No failed certificates found.'}
            </div>
          ) : (
            <>
              <div className="failed-table-wrap">
                <table className="failed-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Registration</th>
                      <th>Valid From</th>
                      <th>Expiry Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentRows.map((val, idx) => (
                      <tr key={val.OID ?? idx}>
                        <td>{indexOfFirst + idx + 1}</td>
                        <td>{val.Narration ?? '--'}</td>
                        <td>{formatISODate(`${val.ValidFrom}`)}</td>
                        <td>{formatISODate(`${val.ExpiryDate}`)}</td>
                        <td>
                          <button
                            className="failed-btn-edit"
                            onClick={() => handleEdit(val.OID)}
                          >
                            EDIT
                          </button>
                          <button
                            className="failed-btn-retry"
                            disabled={retryingOID === val.InsuredItem && genModal}
                            onClick={() => handleRetry(val.InsuredItem)}
                          >
                            <FaRedo style={{ marginRight: 4 }} />
                            Retry
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="failed-pagination">
                <span className="failed-pagination-info">
                  Showing {indexOfFirst + 1}–{Math.min(indexOfFirst + ROWS_PER_PAGE, certificates.length)} of {certificates.length}
                </span>
                <div className="failed-pagination-controls">
                  <button
                    className="failed-page-btn"
                    onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    ‹ Prev
                  </button>
                  {pageNumbers.map((p, i) =>
                    p === '...'
                      ? <span key={`e${i}`} className="failed-page-ellipsis">…</span>
                      : <button
                          key={p}
                          className={`failed-page-btn${currentPage === p ? ' active' : ''}`}
                          onClick={() => setCurrentPage(p)}
                        >
                          {p}
                        </button>
                  )}
                  <button
                    className="failed-page-btn"
                    onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Next ›
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      {/* ── Generation Modal ── */}
      {genModal && (
        <div className="failed-modal-overlay">
          <div className="failed-modal">
            <div className="failed-modal-header">
              <h4>Certificate Generation</h4>
              <button className="failed-modal-close" onClick={closeGenModal} aria-label="Close">&times;</button>
            </div>

            {genStatus === 'pending' && (
              <div className="failed-gen-pending">
                <div className="failed-spinner" />
                <p>Generating certificate, please wait…</p>
              </div>
            )}

            {genStatus === 'success' && (
              <div className="failed-gen-success">
                <a href={certURL} target="_blank" rel="noreferrer">⬇ DOWNLOAD CERTIFICATE</a>
                <p className="failed-disclaimer">
                  If the certificate does not open, click the download button again.
                </p>
              </div>
            )}

            {genStatus === 'error' && (
              <div className="failed-gen-error">
                <div className="failed-error-icon">⚠</div>
                <p>Failed to generate certificate:</p>
                <p><strong>{certError}</strong></p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Edit Details Modal ── */}
      {editModal && (
        <div className="failed-modal-overlay">
          <div className="failed-modal">
            <div className="failed-modal-header">
              <h4>Edit Certificate Details</h4>
              <button className="failed-modal-close" onClick={() => setEditModal(false)} aria-label="Close">&times;</button>
            </div>

            <div className="failed-form-group">
              <label>Client PIN</label>
              <input type="text" value={pin} onChange={e => setPin(e.target.value)} placeholder="Client PIN" />
            </div>

            <div className="failed-form-group">
              <label>Client Email</label>
              <input type="email" value={clientEmail} onChange={e => setClientEmail(e.target.value)} placeholder="Client Email" />
            </div>

            <div className="failed-form-group">
              <label>Client Name</label>
              <input type="text" value={client} onChange={e => setClient(e.target.value)} placeholder="Client Name" />
            </div>

            <div className="failed-form-group">
              <label>Commencement Date</label>
              {!showDatePicker ? (
                <>
                  <input type="text" value={formatISODate(commencementDate)} disabled />
                  <button
                    className="failed-date-toggle change"
                    onClick={() => setShowDatePicker(true)}
                  >
                    Change commencement date
                  </button>
                </>
              ) : (
                <>
                  <input type="date" onChange={handleCommencementChange} />
                  <button
                    className="failed-date-toggle cancel"
                    onClick={() => setShowDatePicker(false)}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>

            <div className="failed-form-group">
              <label>Expiry Date</label>
              <input type="text" value={formatISODate(expiryDate)} disabled />
            </div>

            <div className="failed-form-group">
              <label>Note</label>
              <input type="text" value={note} onChange={e => setNote(e.target.value)} placeholder="Note" />
            </div>

            <button
              className="failed-save-btn"
              onClick={saveChanges}
              disabled={editSaving}
            >
              {editSaving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
